import { Article } from "@faire/help-center-utils/articles";
import { MOBILE_MIN_WIDTH_BREAKPOINTS } from "@faire/help-center-utils/consts/MOBILE_MIN_WIDTH_BREAKPOINTS";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackCxFoundationsRelatedarticleslearnmoreClick } from "@faire/web-api--source/events/cxFoundations/click/relatedarticleslearnmore";
import { trackHelpCenterSelfServeArticleLinkClick } from "@faire/web-api--source/events/helpCenter/click/selfServeArticleLink";
import kebabCase from "lodash/kebabCase";
import React, { useState } from "react";
import styled from "styled-components";

interface IProps {
  articles: Article[];
  viewHasInstantAnswer: boolean;
  onClickOnArticle: (articleId: string) => void;
}

export const ListOfArticles: React.FC<IProps> = ({
  articles,
  viewHasInstantAnswer,
  onClickOnArticle,
}) => {
  const { isTabletAndAbove } = useViewport();
  const maxNumberOfArticles = getMaxNumberOfArticles(
    isTabletAndAbove,
    viewHasInstantAnswer
  );
  const [allArticlesVisible, setAllArticlesVisible] = useState(
    articles.length <= maxNumberOfArticles
  );
  const retailerToken = getRetailerToken();
  const brandToken = getBrandToken();

  const showMoreCtaOnClickHandler = () => {
    setAllArticlesVisible(true);
    trackCxFoundationsRelatedarticleslearnmoreClick(
      retailerToken ?? "",
      brandToken ?? ""
    );
  };

  return (
    <ul>
      {articles.map((article, index) => {
        return (
          <li key={article.id}>
            {allArticlesVisible || index < maxNumberOfArticles ? (
              <Column align="flex-start">
                <Spacer height={index === 0 ? "1/2x" : "2x"} />
                <ArticleItem article={article} onClick={onClickOnArticle} />
              </Column>
            ) : null}
          </li>
        );
      })}
      <ShowMoreCta
        visibleWhen={!allArticlesVisible}
        onClick={showMoreCtaOnClickHandler}
      />
    </ul>
  );
};

const getMaxNumberOfArticles = (
  isTabletAndAbove: boolean,
  hasInstantAnswer: boolean
) => {
  const width = getWindowOrThrow().innerWidth;
  if (isTabletAndAbove) {
    return hasInstantAnswer ? 2 : 5;
  }

  if (width <= MOBILE_MIN_WIDTH_BREAKPOINTS.SMALL) {
    return hasInstantAnswer ? 1 : 4;
  }

  return hasInstantAnswer ? 2 : 6;
};

const ArticleItem: React.FC<{
  article: Article;
  onClick: (articleId: string) => void;
}> = ({ article, onClick }) => {
  const articleLinkClick = () => {
    trackHelpCenterSelfServeArticleLinkClick(article.id, article.name);
    onClick(article.id);
  };
  return (
    <Button
      variant="tertiary"
      linkTo={article.getLink()}
      target="__blank"
      onClick={articleLinkClick}
    >
      <Typography
        data-test-id={kebabCase(article.name)}
        variant="paragraphSansRegular"
      >
        {article.name}
      </Typography>
    </Button>
  );
};

const ShowMoreCta: React.FC<{ visibleWhen: boolean; onClick: () => void }> = ({
  visibleWhen,
  onClick,
}) => {
  if (!visibleWhen) {
    return null;
  }

  return (
    <>
      <Spacer height="2x" />
      <InlineCta
        variant="paragraphSansRegular"
        onClick={onClick}
        data-test-id="show-more-help-articles-btn"
      >
        <StrictLocalMsg
          defaultMessage="+ Show more"
          description={{
            text: "Link/CTA to show more articles in the related help center articles section/card in our help center answers page when there are more than 2 articles",
          }}
        />
      </InlineCta>
    </>
  );
};

const InlineCta = styled(Typography)`
  text-decoration: underline;
  cursor: pointer;
`;
